/**
 * Product engraving / personalization.
 *
 * Responsibilities:
 *   1. <product-personalizer> – the PDP card + popup (live preview, validation, state).
 *   2. Add-to-cart interception – when an engraving is selected, the main product and
 *      the engraving product are added together in a single /cart/add.js request.
 *   3. Cart reconciliation – keeps each engraving line item attached to its parent:
 *        - parent removed  -> engraving removed
 *        - parent quantity changes -> engraving quantity follows
 *   4. Engraving "Remove" buttons rendered inside the cart drawer / cart page.
 *
 * The engraving line item is linked to its parent through two hidden line item
 * properties shared by both lines: `_engraving_key`. The engraving line is
 * identified by the `Personalization` property being "Yes".
 */
(function () {
  if (window.__engravingLoaded) return;
  window.__engravingLoaded = true;

  var theme = window.themeVariables || {};
  var ROUTES = theme.routes || {};
  var SETTINGS = theme.settings || {};

  var CART_ADD = (ROUTES.cartAddUrl || '/cart/add') + '.js';
  var CART_URL = ROUTES.cartUrl || '/cart';
  var CART_CHANGE = (ROUTES.cartChangeUrl || '/cart/change') + '.js';
  var CART_UPDATE = CART_URL + '/update.js';
  var CART_JSON = CART_URL + '.js';

  function isPageCart() {
    return SETTINGS.cartType === 'page' || SETTINGS.pageType === 'cart';
  }

  function dispatchCartEvents(cart, openMiniCart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cart, openMiniCart: !!openMiniCart }
      })
    );
  }

  /* ------------------------------------------------------------------ *
   * 1. PDP custom element
   * ------------------------------------------------------------------ */
  class ProductPersonalizer extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.getAttribute('form-id'));
      this.charLimit = parseInt(this.getAttribute('char-limit'), 10) || 10;
      this.engravingVariantId = parseInt(this.getAttribute('variant-id'), 10);
      this.productTitle = this.getAttribute('product-title') || '';

      this.card = this.querySelector('.personalizer__card');
      this.modal = this.querySelector('[data-personalizer-modal]');
      this.input = this.querySelector('[data-personalizer-input]');
      this.preview = this.querySelector('[data-personalizer-preview]');
      this.remaining = this.querySelector('[data-personalizer-remaining]');
      this.display = this.querySelector('[data-personalizer-display]');
      this.saveBtn = this.querySelector('[data-personalizer-save]');
      this.errorEl = this.querySelector('[data-personalizer-error]');
      this.valueStore = this.querySelector('[data-personalizer-value]');

      // Restore any value that survived a partial re-render.
      this.savedText = (this.valueStore && this.valueStore.value) || '';
      this.currentVariantId = this._readVariantId();

      this._onClick = this._onClick.bind(this);
      this._onInput = this._onInput.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);

      this.addEventListener('click', this._onClick);
      if (this.input) this.input.addEventListener('input', this._onInput);
      document.addEventListener('keydown', this._onKeydown);
      if (this.form) this.form.addEventListener('variant:change', this._onVariantChange);

      this._renderState();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
      if (this.form) this.form.removeEventListener('variant:change', this._onVariantChange);
    }

    get hasEngraving() {
      return this.savedText.trim().length > 0;
    }

    _readVariantId() {
      var input = this.form && this.form.querySelector('input[name="id"]');
      return input && input.value ? input.value : this.currentVariantId || null;
    }

    _onVariantChange(event) {
      if (event.detail && event.detail.variant && event.detail.variant.id) {
        this.currentVariantId = event.detail.variant.id;
      }
    }

    _onClick(event) {
      if (event.target.closest('[data-personalizer-open], [data-personalizer-edit]')) {
        this.openModal();
      } else if (event.target.closest('[data-personalizer-remove]')) {
        this.removeEngraving();
      } else if (event.target.closest('[data-personalizer-close]')) {
        this.closeModal();
      } else if (event.target.closest('[data-personalizer-save]')) {
        this.save();
      }
    }

    _onKeydown(event) {
      if (event.key === 'Escape' && this.modal && !this.modal.hidden) this.closeModal();
    }

    _onInput() {
      var value = this.input.value;
      if (value.length > this.charLimit) {
        value = value.slice(0, this.charLimit);
        this.input.value = value;
      }
      if (this.preview) this.preview.textContent = value;
      if (this.remaining) this.remaining.textContent = Math.max(0, this.charLimit - value.length);

      var valid = value.trim().length > 0;
      if (this.saveBtn) this.saveBtn.disabled = !valid;
      if (this.errorEl && valid) this.errorEl.hidden = true;
    }

    openModal() {
      this.input.value = this.savedText || '';
      this._onInput();
      if (this.errorEl) this.errorEl.hidden = true;
      this.modal.hidden = false;
      document.body.classList.add('personalizer-modal-open');
      var input = this.input;
      setTimeout(function () { input.focus(); }, 60);
    }

    closeModal() {
      this.modal.hidden = true;
      document.body.classList.remove('personalizer-modal-open');
    }

    save() {
      var value = this.input.value;
      if (value.trim().length === 0) {
        if (this.errorEl) this.errorEl.hidden = false;
        if (this.saveBtn) this.saveBtn.disabled = true;
        return;
      }
      // Preserve internal spaces, only strip leading/trailing whitespace.
      this.savedText = value.replace(/^\s+|\s+$/g, '');
      if (this.valueStore) this.valueStore.value = this.savedText;
      this._renderState();
      this.closeModal();
    }

    removeEngraving() {
      this.savedText = '';
      if (this.valueStore) this.valueStore.value = '';
      this._renderState();
    }

    _renderState() {
      var added = this.hasEngraving;
      if (this.card) this.card.setAttribute('data-state', added ? 'added' : 'empty');
      if (this.display) this.display.textContent = this.savedText;
      this.querySelectorAll('[data-personalizer-when-empty]').forEach(function (el) {
        el.hidden = added;
      });
      this.querySelectorAll('[data-personalizer-when-added]').forEach(function (el) {
        el.hidden = !added;
      });
    }

    async handleAddToCart(button) {
      var mainId = this.currentVariantId || this._readVariantId();
      if (!mainId) return;

      var quantity = 1;
      var qtyInput = this.form && this.form.querySelector('[name="quantity"]');
      if (qtyInput && parseInt(qtyInput.value, 10) > 0) {
        quantity = parseInt(qtyInput.value, 10);
      }

      var key = 'eng-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      var items = [
        {
          id: Number(mainId),
          quantity: quantity,
          properties: { _engraving_key: key }
        },
        {
          id: this.engravingVariantId,
          quantity: quantity,
          properties: {
            Personalization: 'Yes',
            'Engraving Text': this.savedText,
            'Parent Product': this.productTitle,
            _parent_variant_id: String(mainId),
            _engraving_key: key
          }
        }
      ];

      var busy = false;
      if (button) {
        busy = button.hasAttribute('disabled');
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-busy', 'true');
      }
      var stop = function () {
        if (button) {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        }
      };
      if (busy) return;

      var responseJson;
      try {
        var response = await fetch(CART_ADD, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: items, sections: ['mini-cart'] })
        });
        responseJson = await response.json();

        if (!response.ok) {
          stop();
          this.dispatchEvent(new CustomEvent('cart-notification:show', {
            bubbles: true,
            cancelable: true,
            detail: { status: 'error', error: (responseJson && responseJson.description) || '' }
          }));
          return;
        }
      } catch (error) {
        stop();
        this.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'error', error: '' }
        }));
        return;
      }

      if (isPageCart()) {
        window.location.href = (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart';
        return;
      }

      try {
        var cart = await (await fetch(CART_JSON)).json();
        cart.sections = responseJson.sections;
        dispatchCartEvents(cart, SETTINGS.cartType === 'drawer');
      } catch (e) {
        this.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      this.dispatchEvent(new CustomEvent('cart-notification:show', {
        bubbles: true,
        cancelable: true,
        detail: { status: 'success' }
      }));

      stop();
    }
  }

  if (!window.customElements.get('product-personalizer')) {
    window.customElements.define('product-personalizer', ProductPersonalizer);
  }

  /* ------------------------------------------------------------------ *
   * 2. Add-to-cart interception (capture phase, survives re-renders)
   * ------------------------------------------------------------------ */
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest('[data-product-add-to-cart-button]');
      if (!button) return;

      // button.form resolves both buttons inside the form and the sticky button
      // that references the form via its `form` attribute.
      var form = button.form || button.closest('form');
      if (!form) return;

      var formId = form.getAttribute('id');
      if (!formId) return;

      var personalizer = document.querySelector(
        'product-personalizer[form-id="' + (window.CSS && CSS.escape ? CSS.escape(formId) : formId) + '"]'
      );
      if (!personalizer || !personalizer.hasEngraving) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      personalizer.handleAddToCart(button);
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * 3. Cart reconciliation (quantity sync + orphan removal)
   * ------------------------------------------------------------------ */
  var reconciling = false;

  async function reconcileCart(cart) {
    if (reconciling) return;

    try {
      if (!cart || !cart.items) {
        cart = await (await fetch(CART_JSON)).json();
      }

      var items = cart.items || [];

      // Map of parent key -> parent quantity (only non-engraving lines).
      var parentQty = {};
      items.forEach(function (item) {
        var props = item.properties || {};
        if (props.Personalization === 'Yes') return;
        if (props._engraving_key) parentQty[props._engraving_key] = item.quantity;
      });

      var updates = {};
      var changed = false;
      items.forEach(function (item) {
        var props = item.properties || {};
        if (props.Personalization !== 'Yes') return;

        var key = props._engraving_key;
        var expected = key ? parentQty[key] : undefined;

        if (expected === undefined) {
          updates[item.key] = 0; // parent gone -> remove engraving
          changed = true;
        } else if (item.quantity !== expected) {
          updates[item.key] = expected; // keep engraving qty in sync with parent
          changed = true;
        }
      });

      if (!changed) return;

      reconciling = true;
      var response = await fetch(CART_UPDATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates, sections: ['mini-cart'] })
      });
      var newCart = await response.json();

      if (isPageCart()) {
        window.location.reload();
        return;
      }
      dispatchCartEvents(newCart, false);
    } catch (error) {
      console.error('[engraving] reconcile failed', error);
    } finally {
      reconciling = false;
    }
  }

  document.documentElement.addEventListener('cart:updated', function (event) {
    if (reconciling) return;
    reconcileCart(event.detail && event.detail.cart);
  });

  /* ------------------------------------------------------------------ *
   * 4. Engraving "Remove" buttons inside the cart
   * ------------------------------------------------------------------ */
  document.addEventListener('click', async function (event) {
    var button = event.target.closest('[data-engraving-remove]');
    if (!button) return;

    event.preventDefault();
    var key = button.getAttribute('data-key');
    if (!key) return;

    button.setAttribute('disabled', 'disabled');

    try {
      var response = await fetch(CART_CHANGE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0, sections: ['mini-cart'] })
      });
      var cart = await response.json();

      if (isPageCart()) {
        window.location.reload();
        return;
      }
      dispatchCartEvents(cart, false);
    } catch (error) {
      console.error('[engraving] remove failed', error);
      button.removeAttribute('disabled');
    }
  });

  /* On the cart page, full reloads bypass the cart:updated event, so run a
   * reconciliation pass once on load to heal orphaned / out-of-sync engravings. */
  if (isPageCart()) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { reconcileCart(); });
    } else {
      reconcileCart();
    }
  }
})();
